import React from 'react';
import { Alert, Box, Button, Checkbox, CircularProgress, FormControlLabel, TextField, Typography } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMagnifyingGlass, faTrash } from '@fortawesome/free-solid-svg-icons';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { FormattedMessage, useIntl } from 'react-intl';
import { TranslationKeys } from 'app/translations';
import { PageWrapperStyled, PreviewGridStyled, ConfirmPanelStyled } from './FileUploadCleanupPage.styles';
import { COLOR_FILE } from './types';
import { PreviewCard } from './components/PreviewCard';
import { FileListCard } from './components/FileListCard';
import { useFileUploadCleanupPage } from './useFileUploadCleanupPage';

// ─── Page ─────────────────────────────────────────────────────────────────────

export const FileUploadCleanupPage = () => {
  const intl = useIntl();
  const {
    locale,
    connectionId,
    setConnectionId,
    sinceDateValue,
    setSinceDateValue,
    previewData,
    previewError,
    isConfirmed,
    setIsConfirmed,
    isDeleting,
    isLoadingPreview,
    countdownSeconds,
    isCountdownActive,
    isFormValid,
    sinceDateISO,
    previewGroups,
    handlePreview,
    handleExecute,
  } = useFileUploadCleanupPage();

  return (
    <PageWrapperStyled>
      {/* ── Form bar ── */}
      <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={locale}>
        <div style={{ display: 'flex', flexDirection: 'row', gap: '1rem', alignItems: 'center', flexWrap: 'wrap' }}>
          <TextField
            label={<FormattedMessage id={TranslationKeys.adminPanel_undoUploadedFile_connectionId} />}
            placeholder={intl.formatMessage({
              id: TranslationKeys.adminPanel_undoUploadedFile_connectionId_placeholder,
            })}
            value={connectionId}
            onChange={e => setConnectionId(e.target.value)}
            variant="outlined"
            sx={{ minWidth: 400 }}
          />
          <DateTimePicker
            label={<FormattedMessage id={TranslationKeys.adminPanel_undoUploadedFile_sinceDate} />}
            value={sinceDateValue}
            onChange={setSinceDateValue}
          />
          <Button
            variant="contained"
            color="primary"
            disabled={!isFormValid || isLoadingPreview}
            startIcon={
              isLoadingPreview ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <FontAwesomeIcon icon={faMagnifyingGlass} />
              )
            }
            onClick={handlePreview}
          >
            <FormattedMessage id={TranslationKeys.adminPanel_undoUploadedFile_previewButton} />
          </Button>
        </div>
      </LocalizationProvider>

      {/* ── Error ── */}
      {previewError && <Alert severity="error">{previewError}</Alert>}

      {/* ── Preview cards ── */}
      {previewGroups.length > 0 && (
        <>
          <PreviewGridStyled>
            {previewGroups.map(group => (
              <PreviewCard key={group.titleId} group={group} />
            ))}
            <FileListCard files={previewData?.uploadedFileDetails ?? []} color={COLOR_FILE} />
          </PreviewGridStyled>

          {/* ── Confirmation panel ── */}
          <ConfirmPanelStyled>
            <Alert severity="error" sx={{ fontWeight: 500 }}>
              <FormattedMessage
                id={TranslationKeys.adminPanel_undoUploadedFile_warningMessage}
                values={{ sinceDate: sinceDateValue?.toLocaleString() ?? sinceDateISO }}
              />
            </Alert>
            <FormControlLabel
              control={
                <Checkbox
                  checked={isConfirmed}
                  onChange={e => setIsConfirmed(e.target.checked)}
                  color="error"
                  disabled={isDeleting}
                />
              }
              label={
                <Typography variant="body2">
                  <FormattedMessage id={TranslationKeys.adminPanel_undoUploadedFile_confirmCheckbox} />
                </Typography>
              }
            />
            <Box>
              <Button
                variant="contained"
                color="error"
                disabled={!isConfirmed || isDeleting || isCountdownActive}
                startIcon={
                  isDeleting ? <CircularProgress size={16} color="inherit" /> : <FontAwesomeIcon icon={faTrash} />
                }
                onClick={handleExecute}
              >
                {isDeleting ? (
                  <FormattedMessage id={TranslationKeys.adminPanel_undoUploadedFile_deletingMessage} />
                ) : isCountdownActive ? (
                  `${countdownSeconds}...`
                ) : (
                  <FormattedMessage id={TranslationKeys.adminPanel_undoUploadedFile_confirmDeletion} />
                )}
              </Button>
            </Box>
          </ConfirmPanelStyled>
        </>
      )}
    </PageWrapperStyled>
  );
};
